import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { classSubjects } from "./subjects.jsx";

function ClassSectionDetails({ classValue, sectionValue, onBack }) {
  const [students, setStudents] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [performanceData, setPerformanceData] = useState([]);
  const [coScholasticData, setCoScholasticData] = useState([]);
  const [traitObservations, setTraitObservations] = useState([]);
  const [remarks, setRemarks] = useState("");

  const [subjectProgressData, setSubjectProgressData] = useState([]);
  const [testComparisonData, setTestComparisonData] = useState([]);

  const isPrepClass = ["Prep I", "Prep II", "Prep III"].includes(classValue);
  const hasCoScholastic = ["6", "7", "8"].includes(classValue);
  const isClasses15 = ["1", "2", "3", "4", "5"].includes(classValue);

  // Helper: infer test type label from the test name (used to pick correct maxMarks part)
  const inferTestType = (testName = "") => {
    const n = String(testName).toLowerCase();
    if (n.includes("unit")) return "unitTest";
    if (n.includes("term")) return "termExam";
    if (n.includes("termexam")) return "termExam";
    if (n.includes("unittest")) return "unitTest";
    return null;
  };

  // Helper: return { value: number, label: string } resolved from maxMarks (number or object)
  const resolveMax = (maxMarks, testName = "") => {
    if (typeof maxMarks === "number") {
      return { value: maxMarks, label: "" };
    }
    if (!maxMarks || typeof maxMarks !== "object") {
      return { value: 0, label: "" };
    }
    const testKey = inferTestType(testName);

    // Prefer explicit keys
    if (testKey && typeof maxMarks[testKey] === "number") {
      const label =
        testKey === "unitTest" ? "Unit Test" : testKey === "termExam" ? "Term Exam" : "";
      return { value: maxMarks[testKey], label };
    }

    // Try common alternate keys
    if (testKey && typeof maxMarks[testKey] !== "number") {
      // fallback: pick first matching key that contains 'unit' or 'term'
      const keys = Object.keys(maxMarks);
      for (const k of keys) {
        if (testKey.includes("unit") && k.toLowerCase().includes("unit") && typeof maxMarks[k] === "number") {
          return { value: maxMarks[k], label: "Unit Test" };
        }
        if (testKey.includes("term") && k.toLowerCase().includes("term") && typeof maxMarks[k] === "number") {
          return { value: maxMarks[k], label: "Term Exam" };
        }
      }
    }

    // If no testName clue, try commonly named props
    if (typeof maxMarks.unitTest === "number") return { value: maxMarks.unitTest, label: "Unit Test" };
    if (typeof maxMarks.termExam === "number") return { value: maxMarks.termExam, label: "Term Exam" };
    if (typeof maxMarks.unit === "number") return { value: maxMarks.unit, label: "Unit Test" };
    if (typeof maxMarks.term === "number") return { value: maxMarks.term, label: "Term Exam" };

    // As a last resort: if object values are numeric, return sum (useful for displaying grand total)
    const numericVals = Object.values(maxMarks).map((v) => Number(v || 0)).filter((v) => !isNaN(v));
    if (numericVals.length > 0) {
      const sum = numericVals.reduce((s, v) => s + v, 0);
      return { value: sum, label: "" };
    }

    // default fallback
    return { value: 0, label: "" };
  };

  const calculateGrade = (obtained, max) => {
    if (obtained === "" || obtained == null) return "";
    const numObtained = parseFloat(obtained);
    const numMax = Number(max) || 0;
    if (isNaN(numObtained) || numObtained > numMax || numMax === 0) return "";
    const percentage = (numObtained / numMax) * 100;
    if (percentage >= 90) return "A+";
    if (percentage >= 80) return "A";
    if (percentage >= 75) return "AB";
    if (percentage >= 70) return "B+";
    if (percentage >= 60) return "B";
    if (percentage >= 40) return "C";
    return "D";
  };

  // Special grade calculation for classes 1-5 with different thresholds
  const calculateGradeForClasses15 = (obtained, max) => {
    if (obtained === "" || obtained == null) return "";
    const numObtained = parseFloat(obtained);
    if (isNaN(numObtained) || numObtained > max || max === 0) return "";
    
    if (max === 100) {
      if (numObtained >= 94) return "A+";
      if (numObtained >= 84) return "A";
      if (numObtained >= 69) return "B+";
      if (numObtained >= 49) return "B";
      if (numObtained >= 39) return "C";
      return "D";
    }
    
    if (max === 50) {
      if (numObtained >= 44) return "A+";
      if (numObtained >= 39) return "A";
      if (numObtained >= 34) return "B+";
      if (numObtained >= 29) return "B";
      if (numObtained >= 19) return "C";
      return "D";
    }
    
    if (max === 25) {
      if (numObtained >= 23) return "A+";
      if (numObtained >= 20) return "A";
      if (numObtained >= 15) return "B+";
      if (numObtained >= 11) return "B";
      if (numObtained >= 9) return "C";
      return "D";
    }
    
    // default fallback
    const percentage = (numObtained / max) * 100;
    if (percentage >= 88) return "A+";
    if (percentage >= 80) return "A";
    if (percentage >= 70) return "B+";
    if (percentage >= 60) return "B";
    if (percentage >= 40) return "C";
    return "D";
  };

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const res = await axios.get("https://aadyaacademy.org/api/students");
        if (Array.isArray(res.data)) {
          const filtered = res.data.filter(
            (stu) =>
              stu.class?.toLowerCase() === classValue.toLowerCase() &&
              stu.section?.toLowerCase() === sectionValue.toLowerCase()
          );
          setStudents(filtered);
        }
      } catch (err) {
        console.error("Failed to fetch students", err);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [classValue, sectionValue]);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const res = await axios.get("https://aadyaacademy.org/api/tests");
        // supports array at res.data or res.data.data
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setTests(list);
        if (list.length > 0) {
          setSelectedTest((prev) => prev || list[0].name);
        }
      } catch (err) {
        console.error("Failed to fetch tests", err);
      }
    };
    fetchTests();
  }, []);

  const determineStream = (scholasticMarks, classVal) => {
    if (!["9", "10"].includes(classVal) || !scholasticMarks) return "science";
    const scienceSubjects = ["physics", "chemistry", "biology"];
    const commerceSubjects = ["commercial_studies"];

    const hasScienceMarks = scienceSubjects.some(
      (subj) => Number(scholasticMarks[subj]) > 0
    );
    const hasCommerceMarks = commerceSubjects.some(
      (subj) => Number(scholasticMarks[subj]) > 0
    );

    if (hasCommerceMarks && !hasScienceMarks) return "commerce";
    return "science";
  };

  const getSubjectsForClass = (classVal, stream) => {
    const result = {};
    if (["9", "10"].includes(classVal)) {
      const subjects = classSubjects[classVal]?.scholastic;
      if (subjects) {
        result.scholastic = [
          ...(subjects.common || []),
          ...(subjects[stream] || subjects.science || []),
        ];
      } else {
        result.scholastic = [];
      }
    } else if (["1", "2", "3", "4", "5"].includes(classVal)) {
      result.scholastic = classSubjects[classVal]?.scholastic || [];
    } else if (["6", "7", "8"].includes(classVal)) {
      result.scholastic = classSubjects[classVal]?.scholastic || [];
      result.coScholastic = classSubjects[classVal]?.coScholastic || [];
    } else if (isPrepClass) {
      // Prep classes - get both scholastic and co-scholastic
      result.scholastic = classSubjects[classVal]?.scholastic || [];
      result.coScholastic = classSubjects[classVal]?.coScholastic || [];
    }
    return result;
  };

  // Custom tooltip for subject charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{`Test: ${label}`}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ margin: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: entry.color, fontWeight: 'bold' }}>{`${entry.name}: `}</span>
              <span>{`${entry.value} marks`}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // UPDATED: Fetch marks for the selected student & selected test
  useEffect(() => {
    const fetchMarks = async () => {
      if (!students[currentStudentIndex] || !selectedTest) return;
      const student = students[currentStudentIndex];
      const studentId = student.student_id;

      try {
        const res = await axios.get("https://aadyaacademy.org/api/student_marks", {
          params: { student_id: studentId, test: selectedTest },
        });

        const scholasticMarks = res.data?.scholastic_marks || {};
        const coScholasticMarks = res.data?.co_scholastic_marks || {};
        const stream = determineStream(scholasticMarks, classValue);
        const subjectsConfig = getSubjectsForClass(classValue, stream);
        const { scholastic, coScholastic } = subjectsConfig;

        let formattedScholastic = [];

        // Handle Scholastic marks for all classes
        if (scholastic && scholastic.length > 0) {
          if (isPrepClass) {
            // Prep classes - show grades only
            formattedScholastic = scholastic.map((subj) => {
              const grade = scholasticMarks?.[subj.id] || "N/A";
              return {
                subject: subj.name,
                spec: subj.spec || "",
                grade,
                scored: null,
                max: null,
                isPrep: true,
              };
            });
          } else if (isClasses15) {
            // Classes 1-5 grouping logic
            const groupedSubjects = scholastic.reduce((acc, subject) => {
              const subjectName = subject.name;
              if (!acc[subjectName]) {
                acc[subjectName] = { specs: [], totalMax: 0 };
              }
              const { value: max } = resolveMax(subject.maxMarks, selectedTest);
              acc[subjectName].specs.push({ ...subject, max });
              acc[subjectName].totalMax += max;
              return acc;
            }, {});

            formattedScholastic = Object.entries(groupedSubjects).flatMap(([subjectName, { specs, totalMax }]) =>
              specs.map((subject, specIndex) => {
                const scoredRaw = scholasticMarks?.[subject.id];
                const scored = scoredRaw === "" || scoredRaw == null ? 0 : Number(scoredRaw) || 0;
                const isLastOfSubject = specIndex === specs.length - 1;
                const totalObtained = specs.reduce((sum, spec) => {
                  const specScoreRaw = scholasticMarks?.[spec.id];
                  const specScore = specScoreRaw === "" || specScoreRaw == null ? 0 : Number(specScoreRaw) || 0;
                  return sum + specScore;
                }, 0);

                return {
                  subject: subjectName,
                  spec: subject.spec,
                  max: subject.max,
                  maxLabel: "",
                  scored,
                  totalObtained: isLastOfSubject ? totalObtained : null,
                  totalMax: isLastOfSubject ? totalMax : null,
                  percent: subject.max > 0 ? Number(((scored / subject.max) * 100).toFixed(1)) : 0,
                  grade: isLastOfSubject ? calculateGradeForClasses15(totalObtained, totalMax) : "",
                  isLastOfSubject,
                };
              })
            );
          } else {
            // Classes 6-10 - individual subjects
            formattedScholastic = scholastic.map((subj) => {
              const { value: max, label: maxLabel } = resolveMax(subj.maxMarks, selectedTest);
              const scoredRaw = scholasticMarks?.[subj.id];
              const scored = scoredRaw === "" || scoredRaw == null ? 0 : Number(scoredRaw) || 0;

              return {
                subject: subj.name,
                spec: subj.spec,
                max,
                maxLabel,
                scored,
                percent: max > 0 ? Number(((scored / max) * 100).toFixed(1)) : 0,
                grade: calculateGrade(scored, max),
              };
            });
          }
        }

        setPerformanceData(formattedScholastic);

        // Handle Co-Scholastic for classes 6-8 AND Prep classes
        const shouldShowCoScholastic = hasCoScholastic || isPrepClass;
        if (shouldShowCoScholastic && coScholastic && coScholastic.length > 0) {
          const formattedCoScholastic = coScholastic.map((subj) => {
            const rawGrade = coScholasticMarks?.[subj.id];
            const scored = Number(rawGrade) || 0;
            const { value: max } = resolveMax(subj.maxMarks || 100, selectedTest);
            const grade = typeof rawGrade === "string" && rawGrade 
              ? rawGrade 
              : calculateGrade(scored, max || 100);
            return {
              subject: subj.name,
              grade: grade || "N/A",
            };
          });
          setCoScholasticData(formattedCoScholastic);
        } else {
          setCoScholasticData([]);
        }

        // Handle Trait Observations for ALL classes that have them defined
        const traitObsConfig = classSubjects[classValue]?.traitObservations || [];
        if (traitObsConfig.length > 0) {
          const formattedTraits = traitObsConfig.map((trait) => {
            const grade = res.data?.traitRemarks?.[trait.id] || 
                         res.data?.traitRemarks?.[trait.name] || 
                         "N/A";
            return {
              subject: trait.name,
              grade,
            };
          });
          setTraitObservations(formattedTraits);
        } else {
          setTraitObservations([]);
        }

        setRemarks(res.data?.remarks || "No remarks provided.");
      } catch (err) {
        console.error("Failed to fetch marks", err);
        setPerformanceData([]);
        setCoScholasticData([]);
        setTraitObservations([]);
        setRemarks("");
      }
    };

    fetchMarks();
  }, [students, currentStudentIndex, selectedTest, classValue, isPrepClass, hasCoScholastic, isClasses15]);

  // Fetch marks across all tests for the current student (for charts)
  useEffect(() => {
    const fetchAllTestMarks = async () => {
      if (!students[currentStudentIndex] || tests.length === 0) return;
      const student = students[currentStudentIndex];
      const studentId = student.student_id;

      let subjectData = [];
      let testData = [];

      try {
        let initialRes = null;
        for (const t of tests) {
          try {
            const r = await axios.get("https://aadyaacademy.org/api/student_marks", {
              params: { student_id: studentId, test: t.name },
            });
            if (r.data?.scholastic_marks) {
              initialRes = r;
              break;
            }
          } catch (err) {
            // ignore and continue
          }
        }
        const stream = determineStream(initialRes?.data?.scholastic_marks, classValue);
        const { scholastic } = getSubjectsForClass(classValue, stream);

        for (const test of tests) {
          try {
            const testRes = await axios.get("https://aadyaacademy.org/api/student_marks", {
              params: { student_id: studentId, test: test.name },
            });

            if (testRes.data?.scholastic_marks && !isPrepClass) {
              const scholasticMarks = testRes.data.scholastic_marks;

              let row = { test: test.name };
              let totalScored = 0;
              let totalMax = 0;

              if (isClasses15) {
                // Group subjects for classes 1-5
                const groupedSubjects = scholastic.reduce((acc, subject) => {
                  const subjectName = subject.name;
                  if (!acc[subjectName]) {
                    acc[subjectName] = { specs: [], totalMax: 0 };
                  }
                  const { value: maxForThisTest } = resolveMax(subject.maxMarks, test.name);
                  acc[subjectName].specs.push({ ...subject, max: maxForThisTest });
                  acc[subjectName].totalMax += maxForThisTest;
                  return acc;
                }, {});

                Object.entries(groupedSubjects).forEach(([subjectName, { specs, totalMax }]) => {
                  const subjectTotal = specs.reduce((sum, spec) => {
                    const specScore = Number(scholasticMarks[spec.id]) || 0;
                    return sum + specScore;
                  }, 0);
                  row[subjectName] = subjectTotal;
                  totalScored += subjectTotal;
                  totalMax += totalMax;
                });
              } else {
                // Other classes
                scholastic.forEach((subj) => {
                  const { value: maxForThisTest } = resolveMax(subj.maxMarks, test.name);
                  const scored = Number(scholasticMarks[subj.id]) || 0;
                  row[subj.name] = scored;
                  totalScored += scored;
                  totalMax += maxForThisTest || 0;
                });
              }

              if (Object.keys(row).length > 1) {
                subjectData.push(row);
              }

              testData.push({
                test: test.name,
                totalMarks: totalScored,
                totalMax: totalMax,
                average: totalMax > 0 ? Number(((totalScored / totalMax) * 100).toFixed(1)) : null,
              });
            }
          } catch (err) {
            console.error(`Failed marks for ${test.name}`, err);
          }
        }

        setSubjectProgressData(subjectData);
        setTestComparisonData(testData.filter((t) => t.totalMarks !== undefined));
      } catch (err) {
        console.error("Failed to fetch initial marks for stream determination", err);
      }
    };

    if (!isPrepClass) {
      fetchAllTestMarks();
    } else {
      setSubjectProgressData([]);
      setTestComparisonData([]);
    }
  }, [students, currentStudentIndex, tests, classValue, isPrepClass, isClasses15]);

  // Get subject keys for LineChart with consistent colors
  const getSubjectKeysWithColors = () => {
    if (subjectProgressData.length === 0) return [];
    
    const subjectKeys = Object.keys(subjectProgressData[0]).filter(key => key !== 'test');
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#a4de6c', 
      '#d0ed57', '#ffbb28', '#ff8042', '#8dd1e1', '#83a6ed'
    ];
    
    return subjectKeys.map((subject, index) => ({
      key: subject,
      color: colors[index % colors.length],
      name: subject
    }));
  };

  const renderScholasticTable = () => {
    if (performanceData.length === 0) return null;

    const testKeyLabel = inferTestType(selectedTest);
    const testHeaderLabel =
      testKeyLabel === "unitTest" ? "Max Marks" : testKeyLabel === "termExam" ? "Max Marks" : "Max";

    if (isPrepClass) {
      // Prep class table - grades only
      return (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>#</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Subject</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Assessment Specification</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {performanceData.map((row, i) => (
              <tr key={i}>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{i + 1}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.subject}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.spec}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>
                  {row.grade || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (isClasses15) {
      // Classes 1-5 with grouped subjects and totals
      return (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>#</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Subject</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Assessment Specification</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Max Marks</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Marks Obtained</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Total Marks</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {performanceData.map((row, i) => (
              <tr 
                key={i} 
                style={{ 
                  borderBottom: row.isLastOfSubject ? "2px solid black" : "1px solid #f0f0f0" 
                }}
              >
                <td style={{ padding: "6px" }}>{i + 1}</td>
                <td style={{ padding: "6px" }}>
                  {row.isLastOfSubject || i === 0 ? row.subject : ""}
                </td>
                <td style={{ padding: "6px" }}>{row.spec}</td>
                <td style={{ padding: "6px" }}>
                  {Number.isFinite(row.max) ? row.max : ""}
                </td>
                <td style={{ padding: "6px" }}>{row.scored}</td>
                <td style={{ padding: "6px" }}>
                  {row.totalObtained !== null ? row.totalObtained : ""}
                </td>
                <td style={{ padding: "6px" }}>
                  {row.isLastOfSubject && row.grade ? row.grade : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      // Regular classes 6-10
      return (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>#</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Subject</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Assessment Specification</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>{testHeaderLabel}</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Scored</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {performanceData.map((row, i) => (
              <tr key={i}>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{i + 1}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.subject}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.spec}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>
                  {Number.isFinite(row.max) ? row.max : ""}
                  {row.maxLabel ? <span style={{ fontSize: "12px", color: "#666" }}> {"(" + row.maxLabel + ")"}</span> : null}
                </td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.scored}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.grade}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  };

  const renderCoScholasticTable = () => {
    if (coScholasticData.length === 0) return null;
    return (
      <div style={{ marginBottom: "20px" }}>
        <h4>Co-Scholastic</h4>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Subject</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {coScholasticData.map((row, i) => (
              <tr key={i}>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.subject}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{row.grade}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTraitObservationsTable = () => {
    if (traitObservations.length === 0) return null;
    return (
      <div style={{ marginBottom: "20px" }}>
        <h4>Observations</h4>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Subject</th>
              <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid #ddd" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {traitObservations.map((trait, i) => (
              <tr key={i}>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{trait.subject}</td>
                <td style={{ padding: "6px", borderBottom: "1px solid #f0f0f0" }}>{trait.grade}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSummary = () => {
    if (isPrepClass) return null;

    const totalMarks = performanceData.reduce((sum, row) => sum + (Number(row.scored) || 0), 0);
    const totalMaxMarks = performanceData.reduce((sum, row) => sum + (Number(row.max) || 0), 0);
    const totalPercent = totalMaxMarks > 0 ? ((totalMarks / totalMaxMarks) * 100).toFixed(2) : 0;
    const totalGrade = calculateGrade(totalMarks, totalMaxMarks);

    return (
      <>
        <h4>Grand Total</h4>
        <p><strong>Total Marks:</strong> {totalMarks} / {totalMaxMarks}</p>
        <p><strong>Percentage:</strong> {totalPercent}%</p>
        <p><strong>Grade:</strong> {totalGrade}</p>
      </>
    );
  };

  if (loading) return <p>Loading students...</p>;
  if (students.length === 0)
    return <p>No students found for class {classValue} / section {sectionValue}.</p>;

  const student = students[currentStudentIndex];
  const hasAnyData = performanceData.length > 0 || coScholasticData.length > 0 || traitObservations.length > 0;

  return (
    <div style={{ padding: "20px", fontSize: "14px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
        <button onClick={onBack} style={{ marginRight: "20px" }}>← Back</button>
        <div style={{ marginLeft: "auto" }}>
          <button
            onClick={() => setCurrentStudentIndex((i) => Math.max(i - 1, 0))}
            disabled={currentStudentIndex === 0}
            style={{ marginRight: "10px" }}
          >
            ← Prev
          </button>
          <span>{currentStudentIndex + 1} of {students.length}</span>
          <button
            onClick={() => setCurrentStudentIndex((i) => Math.min(i + 1, students.length - 1))}
            disabled={currentStudentIndex === students.length - 1}
            style={{ marginLeft: "10px" }}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Student Details */}
      <div style={{ border: "1px solid #ddd", padding: "20px", marginBottom: "30px" }}>
        <h3>Student Details</h3>
        <p><strong>Name:</strong> {student?.student_name}</p>
        <p><strong>ID:</strong> {student?.student_id}</p>
        <p><strong>Class:</strong> {student?.class} - {student?.section}</p>
      </div>

      {/* Test dropdown + tables */}
      <div style={{ border: "1px solid #ccc", padding: "20px" }}>
        <h3>Student Performance Report</h3>
        <div style={{ margin: "15px 0" }}>
          <label><strong>Select Test: </strong></label>
          <select value={selectedTest} onChange={(e) => setSelectedTest(e.target.value)}>
            {tests.map((t) => (
              <option key={t.id ?? t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </div>

        {hasAnyData ? (
          <>
            {/* Scholastic Table */}
            {performanceData.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h4>Scholastic</h4>
                {renderScholasticTable()}
              </div>
            )}

            {/* Co-Scholastic Table */}
            {renderCoScholasticTable()}

            {/* Trait Observations Table */}
            {renderTraitObservationsTable()}

            {/* Remarks + Summary */}
            <div>
              <h4>Remarks</h4>
              <p>{remarks || "No remarks provided."}</p>
              {renderSummary()}
            </div>
          </>
        ) : (
          <p>No marks or observations found for this test.</p>
        )}
      </div>

      {/* Charts */}
      {!isPrepClass && subjectProgressData.length > 0 && (
        <div style={{ marginTop: "30px", display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
          <div>
            <h4>Subject-wise Marks Progress</h4>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={subjectProgressData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="test" angle={0} textAnchor="middle" height={60} interval={0} />
                <YAxis label={{ value: 'Marks Obtained', angle: -90, position: 'insideLeft' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {getSubjectKeysWithColors().map(({ key: subject, color, name }) => (
                  <Line
                    key={subject}
                    type="monotone"
                    dataKey={subject}
                    stroke={color}
                    strokeWidth={3}
                    dot={{ fill: color, strokeWidth: 2, r: 5 }}
                    name={name}
                    activeDot={{ r: 8 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h4>Total Marks Comparison</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={testComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="test" angle={0} textAnchor="middle" height={60} interval={0} />
                <YAxis label={{ value: 'Total Marks', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  formatter={(value) => [`${value} marks`, 'Total Marks']} 
                  labelFormatter={(label) => `Test: ${label}`}
                />
                <Legend />
                <Bar dataKey="totalMarks" fill="#8884d8" name="Total Marks Obtained" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassSectionDetails;